import { InfiniteData, QueryClient } from '@tanstack/react-query'
import uuidv4 from 'uuid/v4'
import { titleFromFirstQuestion } from '../../../components/statsAssistant/utils/conversationTitle.utils'
import {
  DEFAULT_AGENT_PROFILE,
  isOptimisticMessage,
  OPTIMISTIC_MESSAGE_ULID_PREFIX,
  SendMessagePayload,
  StatsConversationDetail,
  StatsConversationList,
  StatsConversationSummary,
  StatsMessage,
} from '../../../model/StatsAssistant'
import { statsAssistantKeys } from './statsAssistant.keys'

const updateConversationLists = (
  queryClient: QueryClient,
  update: (pages: StatsConversationList[]) => StatsConversationList[],
): void => {
  queryClient.setQueriesData<InfiniteData<StatsConversationList>>(
    { queryKey: statsAssistantKeys.conversationsRoot },
    (current) => (current ? { ...current, pages: update(current.pages) } : current),
  )
}

const mapSummary = (
  pages: StatsConversationList[],
  ulid: string,
  patch: Partial<StatsConversationSummary>,
): StatsConversationList[] =>
  pages.map((page) => ({
    ...page,
    items: page.items.map((item) => (item.ulid === ulid ? { ...item, ...patch } : item)),
  }))

export const patchConversationSummary = (
  queryClient: QueryClient,
  ulid: string,
  patch: Partial<StatsConversationSummary>,
): void => updateConversationLists(queryClient, (pages) => mapSummary(pages, ulid, patch))

export const upsertConversationSummary = (
  queryClient: QueryClient,
  summary: StatsConversationSummary,
): void =>
  updateConversationLists(queryClient, (pages) => {
    const isListed = pages.some((page) => page.items.some((item) => item.ulid === summary.ulid))
    if (isListed) return mapSummary(pages, summary.ulid, summary)

    const [firstPage, ...otherPages] = pages
    if (!firstPage) return pages

    return [
      { ...firstPage, items: [summary, ...firstPage.items], total: firstPage.total + 1 },
      ...otherPages,
    ]
  })

export const patchConversationDetail = (
  queryClient: QueryClient,
  ulid: string,
  patch: (current: StatsConversationDetail) => Partial<StatsConversationDetail>,
): void => {
  queryClient.setQueryData<StatsConversationDetail>(
    statsAssistantKeys.conversation(ulid),
    (current) => (current ? { ...current, ...patch(current) } : current),
  )
}

/** Ajoute un message reçu par Centrifugo, sans doublon si le fil a été rechargé entre-temps. */
export const appendConversationMessage = (
  queryClient: QueryClient,
  ulid: string,
  message: StatsMessage,
): void =>
  patchConversationDetail(queryClient, ulid, ({ messages }) => ({
    messages: messages.some((existing) => existing.ulid === message.ulid)
      ? messages
      : [...messages, message],
  }))

const rewindMessages = (
  messages: StatsMessage[],
  replaceFromMessageUlid?: string,
): StatsMessage[] => {
  if (!replaceFromMessageUlid) return messages
  const index = messages.findIndex((message) => message.ulid === replaceFromMessageUlid)
  return index >= 0 ? messages.slice(0, index) : messages
}

export const showOptimisticMessage = (
  queryClient: QueryClient,
  conversationUlid: string,
  { message, replaceFromMessageUlid }: SendMessagePayload,
): void => {
  const conversationKey = statsAssistantKeys.conversation(conversationUlid)
  void queryClient.cancelQueries({ queryKey: conversationKey })

  const current = queryClient.getQueryData<StatsConversationDetail>(conversationKey)
  const title = current?.title ?? titleFromFirstQuestion(message)
  const agentProfile = current?.agentProfile ?? DEFAULT_AGENT_PROFILE
  const optimisticMessage: StatsMessage = {
    ulid: `${OPTIMISTIC_MESSAGE_ULID_PREFIX}${uuidv4()}`,
    role: 'user',
    type: 'text',
    content: { text: message, isFinal: true },
    createdAt: new Date().toISOString(),
  }

  queryClient.setQueryData<StatsConversationDetail>(conversationKey, {
    ulid: conversationUlid,
    title,
    agentProfile,
    isProcessing: true,
    messages: [
      // Une question refusée juste avant reste affichée jusqu'au prochain envoi : on la remplace.
      ...rewindMessages(current?.messages ?? [], replaceFromMessageUlid).filter(
        (existing) => !isOptimisticMessage(existing),
      ),
      optimisticMessage,
    ],
  })
  upsertConversationSummary(queryClient, {
    ulid: conversationUlid,
    title,
    agentProfile,
    isProcessing: true,
    updatedAt: new Date().toISOString(),
    lastMessagePreview: message.slice(0, 120),
  })
}

/**
 * Envoi refusé : la question reste affichée telle que l'utilisateur l'a envoyée, suivie de
 * l'erreur et de « Réessayer ». Seul l'état « en cours » est levé.
 */
export const keepFailedOptimisticMessage = (
  queryClient: QueryClient,
  conversationUlid: string,
): void => {
  patchConversationDetail(queryClient, conversationUlid, () => ({ isProcessing: false }))
  patchConversationSummary(queryClient, conversationUlid, { isProcessing: false })
}

export const settleOptimisticMessage = (
  queryClient: QueryClient,
  conversationUlid: string,
  userMessage: StatsMessage | null,
): void => {
  patchConversationDetail(queryClient, conversationUlid, ({ messages }) => ({
    isProcessing: userMessage !== null,
    messages: messages.flatMap((message) => {
      if (!isOptimisticMessage(message)) return [message]
      return userMessage ? [userMessage] : []
    }),
  }))

  if (!userMessage) {
    patchConversationSummary(queryClient, conversationUlid, { isProcessing: false })
  }
}
